import { useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useUserBadges } from "@/hooks/useUserBadges";
import { badgeLabel } from "@/components/BadgeChip";
import { toast } from "@/lib/toast";

// NewBadgeToaster — invisible component mounted globally in App.tsx. Watches
// the signed-in user's badges; when one appears that wasn't in localStorage
// on the previous render, fires a "🎉 You earned the X badge!" toast.
//
// First-load behavior: when no localStorage entry exists for this user, we
// just record the current badge set without firing toasts. That avoids
// toasting every existing badge on the user's very first session after this
// component shipped. New badges earned after that point will toast.
//
// Storage is keyed by user_id so signing in as a different account doesn't
// inherit the previous user's seen-set.

export function NewBadgeToaster() {
  const { user } = useAuth();
  const { badges } = useUserBadges(user?.id);
  // Track whether we've done the first-render baseline yet, so a transient
  // "badges empty → badges populated" transition during the same session
  // doesn't fire toasts before data has settled.
  const hasBaselined = useRef(false);

  useEffect(() => {
    if (!user?.id) {
      hasBaselined.current = false;
      return;
    }
    if (!badges) return;

    const key = `fig.seenBadges.${user.id}`;
    const currentIds = badges.map((b) => b.id);

    let raw: string | null = null;
    try { raw = localStorage.getItem(key); } catch { /* private mode */ }

    if (raw === null) {
      // First time we've seen this user on this device — baseline silently.
      try { localStorage.setItem(key, JSON.stringify(currentIds)); } catch { /* quota / private */ }
      hasBaselined.current = true;
      return;
    }

    if (!hasBaselined.current) {
      hasBaselined.current = true;
      // Don't toast on the very first render of the component — wait for
      // the next render with fresh data to detect genuine deltas.
      return;
    }

    let seen: Set<string>;
    try {
      seen = new Set(JSON.parse(raw) as string[]);
    } catch {
      // Corrupt entry — reset baseline.
      try { localStorage.setItem(key, JSON.stringify(currentIds)); } catch { /* ignore */ }
      return;
    }

    const newOnes = badges.filter((b) => !seen.has(b.id));
    if (newOnes.length === 0) return;

    for (const b of newOnes) {
      toast.success(`🎉 You earned the ${badgeLabel(b)} badge!`);
    }
    try { localStorage.setItem(key, JSON.stringify(currentIds)); } catch { /* ignore */ }
  }, [user?.id, badges]);

  return null;
}
